/**
 * @file Handles DOM events and checks
 * */
type ContentLoadedCallbackFunction = (event: Event) => void

const callbackQueue: ContentLoadedCallbackFunction[] = []
let isDomContentLoaded = false
let domConentLoadedEvent: Event

const domContentLoadedEvent = (event): void => {
  isDomContentLoaded = true
  domConentLoadedEvent = event
  callbackQueue.forEach((callback) => callback(event))
}
if (document.readyState === 'loading') {
  // Loading hasn't finished yet
  window.addEventListener('DOMContentLoaded', domContentLoadedEvent)
} else {
  // `DOMContentLoaded` has already fired.
  // This could happen if root Vue component (and/or subsequently this bit) is loaded asynchronously
  domContentLoadedEvent(new Event('DOMContentLoaded'))
}

function triggerOnContentLoaded(callback: ContentLoadedCallbackFunction): void {
  if (isDomContentLoaded) {
    callback(domConentLoadedEvent)
  } else {
    callbackQueue.push(callback)
  }
}

function isContentLoaded(): boolean {
  return isDomContentLoaded
}

function isHtmlElementAnInputField(htmlElement: HTMLElement): boolean {
  const tagName = htmlElement?.tagName?.toUpperCase()
  if (!tagName) return false

  const isInputElement =
    ['TEXTAREA', 'INPUT', 'TRIX-EDITOR'].includes(tagName) ||
    htmlElement.contentEditable === 'true' ||
    htmlElement.isContentEditable
  // Trix keeps a copy of the editor in some sort of dom outside the current dom.

  const isLikelyTrixExternalDom = (): boolean => {
    return !document.documentElement.contains(htmlElement)
  }

  const isInTrixEditor = (): boolean => {
    const trixCollection = document.getElementsByTagName('trix-editor')
    for (let i = 0; i < trixCollection.length; i++) {
      const trixElement = trixCollection[i]
      if (trixElement.contains(htmlElement)) return true
    }
    return false
  }

  return isInputElement || isLikelyTrixExternalDom() || isInTrixEditor()
}

// Check if an event's target is an input field, copied from paste_to_post_mixin.ts since we are moving away from it
function isTargetAnInputField(eventTarget?: EventTarget | null): boolean {
  if (!eventTarget) return false
  return isHtmlElementAnInputField(eventTarget as HTMLElement)
}

interface NonStandardClipboardEvent extends ClipboardEvent {
  originalTarget?: EventTarget
  explicitOriginalTarget?: EventTarget
}

function isEventOnAnInputField(event: NonStandardClipboardEvent): boolean {
  // Sometimes, target of pasting will be onto arbitrary #text target
  // In that case, we get originalTarget, or even explicitOriginalTarget
  return (
    isTargetAnInputField(event.target) ||
    isTargetAnInputField(event.originalTarget) ||
    isTargetAnInputField(event.explicitOriginalTarget)
  )
}

function isActiveElementAnInputField(): boolean {
  const activeElement = document?.activeElement
  return isHtmlElementAnInputField(activeElement as HTMLElement)
}

export { triggerOnContentLoaded, isContentLoaded, isEventOnAnInputField, isActiveElementAnInputField }
